import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Send, MessageSquare, Sparkles, User as UserIcon, Activity, ArrowLeft, Play, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/app/AppShell";
import AdSlot from "@/components/app/AdSlot";
import { ADSENSE_SLOTS, isValidAdSlot } from "@/config/adsense";
import { IconAvatar } from "@/components/app/IconAvatar";
import {
  claimInactiveMatchCredit, getChatMessages, getChatThreads, getChatWebSocketUrl, markChatRead,
  markMatchSeen, sendChatMessage, unlockChatWithAd,
} from "@/lib/api";

export default function Chat() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [limits, setLimits] = useState(null);
  const [showAdUnlock, setShowAdUnlock] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [adStatus, setAdStatus] = useState(null);
  const scrollRef = useRef(null);
  const socketRef = useRef(null);

  const selected = useMemo(() => threads.find((thread) => thread.matchId === matchId) || null, [threads, matchId]);
  const selectedMatchId = selected?.matchId;
  const partner = selected?.partner?.profile;
  const chatAdSlot = ADSENSE_SLOTS.chat || ADSENSE_SLOTS.discover;
  const chatAdConfigured = isValidAdSlot(chatAdSlot);
  const adRewardReady = process.env.NODE_ENV !== "production" || (chatAdConfigured && adStatus === "filled");

  useEffect(() => {
    let cancelled = false;
    getChatThreads()
      .then((response) => { if (!cancelled) setThreads(response.threads || []); })
      .catch((error) => toast("Could not load chats.", { description: error.message }))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedMatchId) return;
    let cancelled = false;
    Promise.all([getChatMessages(selectedMatchId), markChatRead(selectedMatchId), markMatchSeen(selectedMatchId)])
      .then(([page]) => {
        if (!cancelled) {
          setMessages(page.messages || []);
          setLimits(page.limits || null);
          setThreads((items) => items.map((thread) => thread.matchId === selectedMatchId ? { ...thread, unreadCount: 0, isNewMatch: false } : thread));
        }
      })
      .catch((error) => toast("Could not load messages.", { description: error.message }));
    return () => { cancelled = true; };
  }, [selectedMatchId]);

  useEffect(() => {
    if (!selectedMatchId) return undefined;
    const socket = new WebSocket(getChatWebSocketUrl(selectedMatchId));
    socketRef.current = socket;
    socket.onmessage = (event) => {
      const payload = JSON.parse(event.data);
      if (payload.type !== "message.created") return;
      const message = payload.message;
      setMessages((items) => items.some((item) => item.id === message.id) ? items : [...items, message]);
      if (message.sender === "them") {
        setLimits((current) => current?.unlimited ? current : { ...current, sentSinceReply: 0 });
        setThreads((items) => items.map((thread) => thread.matchId === selectedMatchId ? { ...thread, inactivity: { ...thread.inactivity, visible: false, eligible: false } } : thread));
      }
      setThreads((items) => items.map((thread) => thread.matchId === selectedMatchId ? { ...thread, lastMessage: message } : thread));
    };
    socket.onerror = () => socket.close();
    return () => socket.close();
  }, [selectedMatchId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const body = input.trim();
    if (!selected || !body) return;
    setInput("");
    const clientMessageId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    try {
      const message = await sendChatMessage(selected.matchId, body, clientMessageId);
      setMessages((items) => items.some((item) => item.id === message.id) ? items : [...items, message]);
      setLimits((current) => current?.unlimited ? current : current ? {
        ...current,
        sentSinceReply: (current.sentSinceReply || 0) + 1,
        sentToday: (current.sentToday || 0) + 1,
        adMessagesRemaining: Math.max(0, (current.adMessagesRemaining || 0) - 1),
      } : current);
      setThreads((items) => items.map((thread) => thread.matchId === selected.matchId ? { ...thread, lastMessage: message } : thread));
    } catch (error) {
      setInput(body);
      if (error.code === "CHAT_AD_REQUIRED") {
        setAdStatus(null);
        setShowAdUnlock(true);
      }
      toast("Could not send message.", { description: error.message });
    }
  };

  const claimInactive = async () => {
    if (!selected) return;
    if (!window.confirm("Close this inactive match and restore your match capacity? This cannot be undone.")) return;
    try {
      await claimInactiveMatchCredit(selected.matchId);
      setThreads((items) => items.filter((thread) => thread.matchId !== selected.matchId));
      toast("Match closed and capacity restored.");
      navigate("/chat");
    } catch (error) {
      toast("Could not complete the inactivity claim.", { description: error.message });
    }
  };

  const completeAdUnlock = async () => {
    if (!selected) return;
    setUnlocking(true);
    try {
      const result = await unlockChatWithAd(selected.matchId);
      setLimits((current) => ({ ...current, adMessagesRemaining: result.remainingMessages }));
      setShowAdUnlock(false);
      toast("Two messages unlocked.", { description: "Use them whenever a reply or daily limit blocks you." });
    } catch (error) {
      toast("Could not unlock messages.", { description: error.message });
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <AppShell>
      <div className="grid lg:grid-cols-[320px_1fr] h-screen">
        <aside className={`border-r border-white/5 overflow-y-auto ${selected ? "hidden lg:block" : "block"}`}>
          <div className="px-6 py-6 border-b border-white/5"><div className="overline mb-2">Chats</div><h2 className="font-serif-editorial text-2xl leading-tight">Threads</h2></div>
          {loading ? <div className="p-6 text-sm text-[#A1A1AA]">Loading chats...</div> : threads.length === 0 ? (
            <div className="p-6 text-sm text-[#A1A1AA] font-light">No chats yet. <Link to="/discover" className="text-[#F59E0B] hover:underline">Find a match</Link></div>
          ) : <ul className="p-3 space-y-1">{threads.map((thread) => {
            const profile = thread.partner.profile;
            const active = thread.matchId === matchId;
            return <li key={thread.matchId}><button onClick={() => navigate(`/chat/${thread.matchId}`)} className={`w-full text-left flex items-center gap-3 px-3 py-3 rounded-md ${active ? "bg-[#F59E0B]/10" : "hover:bg-white/[0.03]"}`}>
              <IconAvatar iconKey={profile.profileIcon} size="sm" tone="outline" /><div className="min-w-0 flex-1"><div className="flex items-center gap-2"><span className="text-sm truncate">{profile.fullName.split(" ")[0]}</span>{(thread.isNewMatch || thread.unreadCount > 0) && <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" />}</div><div className="text-[11px] text-[#A1A1AA] truncate">{thread.lastMessage?.body || "New match - say hi."}</div></div>
            </button></li>;
          })}</ul>}
        </aside>
        <section className="flex flex-col h-full min-h-0">
          {!selected ? <div className="flex-1 flex items-center justify-center p-10"><div className="hm-card p-10 rounded-md text-center max-w-md"><MessageSquare className="w-8 h-8 text-[#F59E0B] mx-auto mb-4" strokeWidth={1.25} /><h2 className="font-serif-editorial text-2xl mb-3">Pick a thread.</h2><p className="text-[#A1A1AA] font-light">Feel the fit before you commit. First message is on you.</p></div></div> : <>
            <div className="border-b border-white/5 bg-black/40 backdrop-blur-xl px-6 py-4 flex items-center gap-3"><button onClick={() => navigate("/chat")} className="lg:hidden text-[#A1A1AA] hover:text-[#F5F5F0]"><ArrowLeft className="w-5 h-5" /></button><IconAvatar iconKey={partner.profileIcon} size="md" /><div className="min-w-0 flex-1"><div className="font-serif-editorial text-lg leading-tight">{partner.fullName}</div><div className="text-xs text-[#A1A1AA] truncate">{(partner.goalTags || []).slice(0, 2).join(" · ")} · {partner.timezone}</div></div><div className="flex items-center gap-2"><Link to={`/profile/${selected.partner.userId}`} className="hm-btn-ghost inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs"><UserIcon className="w-3.5 h-3.5" /> Profile</Link>{selected.partnershipId && !selected.partnershipPending && !selected.partnershipEnded ? <Link to={`/workspace/${selected.partnershipId}`} className="hm-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold"><Activity className="w-3.5 h-3.5" /> Workspace</Link> : selected.partnershipEnded ? <span className="text-xs text-[#7a7a80]">Partnership ended</span> : <Link to="/matches" className="hm-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold"><Sparkles className="w-3.5 h-3.5" /> {selected.needsYourConfirmation ? "Join partnership" : selected.partnershipStartedByMe ? "Awaiting confirmation" : "Start partnership"}</Link>}</div></div>
            {selected.inactivity?.visible && <ChatInactivityNotice inactivity={selected.inactivity} partnerName={partner.fullName.split(" ")[0]} onClaim={claimInactive} />}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-3">{messages.map((message) => <div key={message.id} className={`flex ${message.sender === "me" ? "justify-end" : "justify-start"}`}><div className={`max-w-[70%] px-4 py-2.5 rounded-md ${message.sender === "me" ? "bg-[#F59E0B] text-black rounded-br-sm" : "bg-[#141414] border border-white/5 rounded-bl-sm"}`}><div className="text-sm leading-relaxed font-light whitespace-pre-wrap">{message.body}</div><div className={`text-[10px] mt-1 ${message.sender === "me" ? "text-black/60" : "text-[#7a7a80]"}`}>{new Date(message.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div></div></div>)}</div>
            <div className="border-t border-white/5 px-6 py-4 bg-black/40">
              {limits && !limits.unlimited && (
                <div className="flex items-center justify-between gap-3 mb-2 text-[11px] text-[#A1A1AA]">
                  <span>{limits.sentToday}/{limits.dailyLimit} today · {limits.sentSinceReply}/{limits.burstLimit} before reply</span>
                  {limits.adMessagesRemaining > 0 ? <span className="text-[#F59E0B]">{limits.adMessagesRemaining} ad-unlocked left</span> : null}
                </div>
              )}
              <div className="flex gap-2"><input type="text" value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => event.key === "Enter" && send()} placeholder="Send an intentional message..." className="hm-input flex-1 px-4 py-3 rounded-md text-sm font-light placeholder:text-[#5b5b60]" /><button onClick={send} className="hm-btn-primary inline-flex items-center gap-2 px-5 rounded-md text-sm font-semibold"><Send className="w-4 h-4" strokeWidth={1.75} /></button></div>
            </div>
          </>}
        </section>
      </div>
      {showAdUnlock && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" role="dialog" aria-modal="true">
          <div className="hm-card w-full max-w-lg rounded-md p-6">
            <div className="overline mb-2">Free chat · extra messages</div>
            <h2 className="font-serif-editorial text-2xl mb-2">View this ad to unlock 2 messages.</h2>
            <p className="text-sm text-[#A1A1AA] mb-4">No click or interaction with the ad is required. Your partner can still reply at any time, which resets the two-message window.</p>
            <AdSlot slot={chatAdSlot} onStatusChange={setAdStatus} className="min-h-[250px] mb-3" />
            <p className="text-[11px] text-[#A1A1AA] mb-5">{!chatAdConfigured ? "A chat ad unit has not been configured yet." : adStatus === "filled" ? "The ad is showing. You can continue when ready." : adStatus === "unfilled" || adStatus === "unfill-optimized" ? "No ad is available right now. Try again later." : "Loading an available ad..."}</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowAdUnlock(false)} className="hm-btn-ghost px-4 py-2 rounded-md text-xs">Cancel</button>
              <button onClick={completeAdUnlock} disabled={unlocking || !adRewardReady} className="hm-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold"><Play className="w-3.5 h-3.5" />{unlocking ? "Unlocking..." : adRewardReady ? "Continue · unlock" : "Waiting for ad..."}</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function ChatInactivityNotice({ inactivity, partnerName, onClaim }) {
  return (
    <div className="border-b border-amber-500/20 bg-amber-500/[0.05] px-6 py-3 text-xs">
      <div className="flex flex-wrap items-center gap-3">
        <AlertTriangle className="h-4 w-4 text-amber-300" />
        <span className="flex-1 text-[#A1A1AA]">{partnerName} has not replied or joined yet. {inactivity.eligible ? "You can now close this inactive match and restore your match capacity." : `The inactivity claim unlocks in ${inactivity.daysUntilEligible} day${inactivity.daysUntilEligible === 1 ? "" : "s"}.`}</span>
        <button onClick={onClaim} disabled={!inactivity.eligible} className="hm-btn-ghost px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-50">Report inactivity & restore match</button>
      </div>
    </div>
  );
}
